import { gql } from '@apollo/client';
import { getDefaultGraphqlClient } from '@core/api/graphqlClient';
import {
  apolloContextForShop,
  apolloContextUnscopedShopList,
  type HasuraApolloContext,
} from '@core/api/apolloContext';
import {
  logCheckoutAddress,
  warnCheckoutAddress,
} from '@core/utils/checkoutAddressDebugLog';
import { maskPhone } from '@shared/utils/phoneIdentity';

/** Tenant-scoped `mst_users` row (matches Auth `UserData` shape for Hasura picks). */
export interface TenantMstUserRow {
  id: string;
  name: string;
  /** Canonical customer phone stored as 10 local digits (no country prefix). */
  mobile: string;
  /** User-provided normal contact email. */
  email?: string | null;
  shop_id: string | null;
  user_type: string;
  created_at: string;
  updated_at: string;
}

interface GetPlatformRegistryRowResponse {
  mst_users: TenantMstUserRow[];
}

interface GetUserByMobileResponse {
  mst_users: TenantMstUserRow[];
}

export const GET_USER_BY_MOBILE = gql`
  query GetUserByMobile($mobile: String!, $shopId: uuid!) {
    mst_users(
      where: {
        mobile: { _eq: $mobile }
        shop_id: { _eq: $shopId }
      }
      limit: 1
    ) {
      id
      name
      mobile
      email
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

export const GET_USER_BY_MOBILE_IREGEX = gql`
  query GetUserByMobileIRegex($pattern: String!, $shopId: uuid!) {
    mst_users(
      where: {
        mobile: { _iregex: $pattern }
        shop_id: { _eq: $shopId }
      }
      limit: 1
    ) {
      id
      name
      mobile
      email
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

const GET_MST_USERS_PLATFORM_REGISTRY = gql`
  query GetPlatformRegistryMstUser($mobile: String!) {
    mst_users(
      where: {
        mobile: { _eq: $mobile }
        shop_id: { _is_null: true }
      }
      limit: 1
    ) {
      id
      name
      mobile
      email
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

const GET_MST_USERS_PLATFORM_REGISTRY_IREGEX = gql`
  query GetPlatformRegistryMstUserIRegex($pattern: String!) {
    mst_users(
      where: {
        mobile: { _iregex: $pattern }
        shop_id: { _is_null: true }
      }
      limit: 1
    ) {
      id
      name
      mobile
      email
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

export function normalizeAuthEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

export function exactIregexPattern(lowerValue: string): string {
  return `^${lowerValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;
}

function normalizeGeneratedEmail(generatedEmail: string | null | undefined): string {
  return normalizeAuthEmail(generatedEmail);
}

function normalizeStoredMobile(mobile: string | null | undefined): string {
  return String(mobile ?? '').trim();
}

const UPDATE_MST_USER_MOBILE = gql`
  mutation UpdateMstUserMobile($id: uuid!, $mobile: String!) {
    update_mst_users_by_pk(pk_columns: { id: $id }, _set: { mobile: $mobile }) {
      id
      name
      mobile
      email
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

async function migrateLegacyGeneratedMobile(
  row: TenantMstUserRow,
  mobile: string,
  context: HasuraApolloContext
): Promise<TenantMstUserRow> {
  if (row.mobile === mobile) return row;
  try {
    const result = await getDefaultGraphqlClient().mutate<{
      update_mst_users_by_pk: TenantMstUserRow | null;
    }>({
      mutation: UPDATE_MST_USER_MOBILE,
      variables: { id: row.id, mobile },
      context,
    });
    return result.data?.update_mst_users_by_pk ?? { ...row, mobile };
  } catch (error) {
    warnCheckoutAddress('mst_users.mobile migration failed', {
      mst_usersId: row.id,
      targetMobile: maskPhone(mobile),
      error: error instanceof Error ? error.message : String(error),
    });
    return row;
  }
}

function logGQLErrors(
  scope: string,
  res: {
    error?: { message?: string; graphQLErrors?: ReadonlyArray<{ message: string }> };
  },
  tag: string
): void {
  if (!res.error) return;
  warnCheckoutAddress(scope + ' Apollo error', {
    tag: maskPhone(tag),
    message: res.error.message,
    graphQLMessages:
      res.error.graphQLErrors?.map((x: { message: string }) => x.message) ?? [],
  });
}

async function fetchPlatformRegistryByExactMobile(
  mobile: string
): Promise<TenantMstUserRow | null> {
  const client = getDefaultGraphqlClient();
  const context = apolloContextUnscopedShopList();
  const result = await client.query<GetPlatformRegistryRowResponse>({
    query: GET_MST_USERS_PLATFORM_REGISTRY,
    variables: { mobile },
    fetchPolicy: 'network-only',
    context,
  });
  logGQLErrors('mst_users.platform-registry phone lookup:_eq', result, mobile);
  return result.data?.mst_users?.[0] ?? null;
}

export async function fetchMstUserPlatformRegistryByMobile(
  mobile: string,
  legacyGeneratedEmail?: string
): Promise<TenantMstUserRow | null> {
  const normalized = normalizeStoredMobile(mobile);
  if (!normalized) return null;

  logCheckoutAddress('mst_users.platform-registry phone lookup:start', {
    mobile: maskPhone(normalized),
  });

  const client = getDefaultGraphqlClient();
  const context = apolloContextUnscopedShopList();

  try {
    const eqRow = await fetchPlatformRegistryByExactMobile(normalized);
    if (eqRow?.id) {
      logCheckoutAddress('mst_users.platform-registry phone lookup:_eq hit', {
        mst_usersId: eqRow.id,
      });
      return eqRow;
    }

    const ci = await client.query<GetPlatformRegistryRowResponse>({
      query: GET_MST_USERS_PLATFORM_REGISTRY_IREGEX,
      variables: { pattern: exactIregexPattern(normalized) },
      fetchPolicy: 'network-only',
      context,
    });
    logGQLErrors('mst_users.platform-registry phone lookup:iregex', ci, normalized);
    const row = ci.data?.mst_users?.[0];
    if (row?.id) {
      logCheckoutAddress('mst_users.platform-registry phone lookup:iregex hit', {
        mst_usersId: row.id,
      });
      return row;
    }

    const legacy = normalizeGeneratedEmail(legacyGeneratedEmail);
    if (legacy) {
      const legacyRow = await fetchPlatformRegistryByExactMobile(legacy);
      if (legacyRow?.id) {
        logCheckoutAddress('mst_users.platform-registry legacy generated-email mobile hit', {
          mst_usersId: legacyRow.id,
        });
        return migrateLegacyGeneratedMobile(legacyRow, normalized, context);
      }
    }

    const fallbackRegistryShop =
      typeof import.meta.env.VITE_PLATFORM_REGISTRY_SHOP_ID === 'string'
        ? import.meta.env.VITE_PLATFORM_REGISTRY_SHOP_ID.trim()
        : '';
    if (fallbackRegistryShop) {
      const anchored = await fetchMstUserRowByMobileForShop(
        normalized,
        fallbackRegistryShop,
        legacy
      );
      if (
        anchored?.id &&
        (anchored.shop_id === fallbackRegistryShop || anchored.shop_id == null)
      ) {
        logCheckoutAddress(
          'mst_users.platform-registry phone lookup:hit via VITE_PLATFORM_REGISTRY_SHOP_ID',
          { mst_usersId: anchored.id, shop_id: anchored.shop_id }
        );
        return anchored;
      }
    }

    logCheckoutAddress('mst_users.platform-registry phone lookup:miss', {
      mobile: maskPhone(normalized),
    });
    return null;
  } catch (e) {
    warnCheckoutAddress('mst_users.platform-registry phone lookup threw', {
      error: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

/**
 * Resolve `mst_users.id` for the canonical phone stored in `mst_users.mobile`.
 * If a previous deployment wrote the generated Firebase email into `mobile`, this
 * detects that row and rewrites it to the canonical 10-digit phone value.
 */
export async function fetchMstUserRowByMobileForShop(
  mobile: string,
  shopId: string,
  legacyGeneratedEmail?: string
): Promise<TenantMstUserRow | null> {
  const normalized = normalizeStoredMobile(mobile);
  if (!normalized) {
    logCheckoutAddress('mst_users.mobile+shop phone lookup:skipped', {
      reason: 'empty-mobile',
      shopId,
    });
    return null;
  }

  logCheckoutAddress('mst_users.mobile+shop phone lookup:request', {
    mobile: maskPhone(normalized),
    shopId,
  });

  const client = getDefaultGraphqlClient();
  const context = apolloContextForShop(shopId);

  const runExact = async (targetMobile: string): Promise<TenantMstUserRow | null> => {
    const result = await client.query<GetUserByMobileResponse>({
      query: GET_USER_BY_MOBILE,
      variables: { mobile: targetMobile, shopId },
      fetchPolicy: 'network-only',
      context,
    });
    logGQLErrors('mst_users.mobile+shop phone lookup:_eq', result, targetMobile);
    return result.data?.mst_users?.[0] ?? null;
  };

  try {
    const eqRow = await runExact(normalized);
    if (eqRow?.id) {
      logCheckoutAddress('mst_users.mobile+shop phone lookup:_eq hit', {
        shopId,
        mst_usersId: eqRow.id,
        row_shop_id: eqRow.shop_id,
        user_type: eqRow.user_type,
      });
      return eqRow;
    }

    const iregexPattern = exactIregexPattern(normalized);
    const ci = await client.query<GetUserByMobileResponse>({
      query: GET_USER_BY_MOBILE_IREGEX,
      variables: { pattern: iregexPattern, shopId },
      fetchPolicy: 'network-only',
      context,
    });
    logGQLErrors('mst_users.mobile+shop phone lookup:iregex', ci, normalized);
    const row = ci.data?.mst_users?.[0];
    if (row?.id) {
      logCheckoutAddress('mst_users.mobile+shop phone lookup:iregex hit', {
        shopId,
        mst_usersId: row.id,
        row_shop_id: row.shop_id,
      });
      return row;
    }

    const legacy = normalizeGeneratedEmail(legacyGeneratedEmail);
    if (legacy) {
      const legacyRow = await runExact(legacy);
      if (legacyRow?.id) {
        logCheckoutAddress('mst_users.mobile+shop legacy generated-email mobile hit', {
          shopId,
          mst_usersId: legacyRow.id,
        });
        return migrateLegacyGeneratedMobile(legacyRow, normalized, context);
      }
    }

    logCheckoutAddress('mst_users.mobile+shop phone lookup:miss', {
      shopId,
      mobile: maskPhone(normalized),
    });
    return null;
  } catch (e) {
    warnCheckoutAddress('mst_users.mobile+shop phone lookup threw', {
      shopId,
      error: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}
